// @ts-nocheck
import React, {
    createContext,
    useContext,
    useState,
    useRef,
    useCallback,
} from 'react';
import { cn } from "@/lib/utils"
import { useForm as useReactHookForm, FormProvider as ReactHookFormProvider } from 'react-hook-form';



/** -------- Types -------- */
export interface FormData {
    [fieldName: string]: string | number | boolean | File | undefined;
}

export interface ValidationRule {
    required?: boolean;
    minLength?: number;
    maxLength?: number;
    min?: number;
    max?: number;
    pattern?: 'email' | 'phone' | 'url' | string; // regex pattern or predefined type
    custom?: (value: any) => string | null; // custom validation function
}

export interface ValidationRules {
    [fieldName: string]: ValidationRule;
}

interface WvcFormContextType {
    // Form identification
    formId: string;
    sectionName: string;

    // State
    isSubmitting: boolean;
    isSubmitted: boolean;
    submitError: string | null;
    successMessage: string;

    // Actions
    handleSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
    resetForm: () => void;

    // Form methods (expose react-hook-form methods)
    control: any;
    formState: any;
    register: any;
    setValue: any;
    getValues: any;
    watch: any;
}

type FormProviderProps = {
    children: React.ReactNode;
    formId: string;
    sectionName: string;
    validationRules?: ValidationRules;
    submitText?: string;
    successMessage?: string;
    errorMessage?: string;
    defaultValues?: any;
    formVersion?: string;
} & React.HTMLAttributes<HTMLDivElement>;

// Create context for WVC-specific form functionality
const WvcFormContext = createContext<WvcFormContextType | null>(null);

// Custom hook to use WVC form context
export const useWvcForm = () => {
    const context = useContext(WvcFormContext);
    if (!context) {
        throw new Error('useWvcForm must be used within a FormProvider');
    }
    return context;
};

// Note: Do NOT re-export useForm to prevent confusion with useWvcForm
// Always use useWvcForm() inside FormProvider components

// Predefined validation patterns
const VALIDATION_PATTERNS = {
    email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    phone: /^[\+]?[1-9][\d]{0,15}$/,
    url: /^https?:\/\/.+/,
};

// Helper function to convert validation rules to react-hook-form format
const convertValidationRules = (rules: ValidationRules) => {
    const rhfRules: any = {};

    Object.entries(rules).forEach(([fieldName, rule]) => {
        rhfRules[fieldName] = {};

        if (rule.required) {
            rhfRules[fieldName].required = `${fieldName} is required`;
        }

        if (rule.minLength) {
            rhfRules[fieldName].minLength = {
                value: rule.minLength,
                message: `${fieldName} must be at least ${rule.minLength} characters`
            };
        }

        if (rule.maxLength) {
            rhfRules[fieldName].maxLength = {
                value: rule.maxLength,
                message: `${fieldName} must be no more than ${rule.maxLength} characters`
            };
        }

        if (rule.pattern) {
            let pattern: RegExp;
            if (typeof rule.pattern === 'string' && VALIDATION_PATTERNS[rule.pattern as keyof typeof VALIDATION_PATTERNS]) {
                pattern = VALIDATION_PATTERNS[rule.pattern as keyof typeof VALIDATION_PATTERNS];
            } else if (typeof rule.pattern === 'string') {
                pattern = new RegExp(rule.pattern);
            } else {
                pattern = rule.pattern;
            }

            rhfRules[fieldName].pattern = {
                value: pattern,
                message: `Please enter a valid ${typeof rule.pattern === 'string' ? rule.pattern : 'format'}`
            };
        }

        if (rule.custom) {
            rhfRules[fieldName].validate = rule.custom;
        }
    });

    return rhfRules;
};

// FormProvider component that wraps react-hook-form
const FormProvider: React.FC<FormProviderProps> = ({
    children,
    formId,
    sectionName,
    validationRules = {},
    submitText = "Submit",
    successMessage = "Form submitted successfully!",
    errorMessage = "Failed to submit form. Please try again.",
    defaultValues = {},
    formVersion = "1.0.0",
    ...divProps
}) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const submissionAttemptRef = useRef(0);

    // Initialize react-hook-form with safe default values
    const methods = useReactHookForm({
        defaultValues: defaultValues || {},
        mode: 'onBlur', // Validate on blur for better UX
    });

    const { handleSubmit: rhfHandleSubmit, reset, formState } = methods;

    // Submit form
    const handleSubmit = useCallback(async (data: any) => {
        if (isSubmitting) return;

        setIsSubmitting(true);
        setSubmitError(null);
        submissionAttemptRef.current += 1;

        try {
            // Prepare submission data
            const submissionData = {
                // Required fields
                sectionName,
                formId,
                formData: data,

                // Auto-generated fields
                timestamp: Date.now(),
                sessionId: wvcClient.getSessionId?.() || 'unknown',
                pageUrl: window.location.href,
                userAgent: navigator.userAgent,

                // Optional metadata
                validationErrors: formState.errors || null,
                submissionAttempt: submissionAttemptRef.current,
                formVersion,

                // Additional metadata can be added here
                validationRules,
            };

            // Call wvcClient.formSubmission
            const submissionResponse = await wvcClient.formSubmission(submissionData);



            // Success
            setIsSubmitted(true);
            setSubmitError(null);

        } catch (error) {
            console.error('Form submission error:', error);
            setSubmitError(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    }, [
        isSubmitting,
        sectionName,
        formId,
        formState.errors,
        errorMessage,
    ]);

    // Reset form
    const resetForm = useCallback(() => {
        reset();
        setIsSubmitted(false);
        setSubmitError(null);
        submissionAttemptRef.current = 0;
    }, [reset]);

    const wvcFormValue: WvcFormContextType = {
        formId,
        sectionName,
        isSubmitting,
        isSubmitted,
        submitError,
        successMessage,
        handleSubmit: rhfHandleSubmit(handleSubmit),
        resetForm,
        // Expose react-hook-form methods
        control: methods.control,
        formState: methods.formState,
        register: methods.register,
        setValue: methods.setValue,
        getValues: methods.getValues,
        watch: methods.watch,
    };

    return (
        <ReactHookFormProvider {...methods}>
            <WvcFormContext.Provider value={wvcFormValue}>
                <div
                    {...divProps}
                    data-wvc-dynamic="FormProvider"
                    data-wvc-formId={formId}
                >
                    {children}
                </div>
            </WvcFormContext.Provider>
        </ReactHookFormProvider>
    );
}; export { FormProvider };
